/// <reference types="web-bluetooth" />

import React from 'react';
import logo from './logo.svg';
import './App.css';
import { SensorManager } from './sensors/SensorManager';
import { CyclingPowerEventData } from "./sensors/CyclingPowerEventData";
import { Activity } from './activities/Activity';
import { MillisToHMS } from './utils/Time';
import { Recorder } from './activities/Recorder';
import FileSaver from 'file-saver';
import { ActivityToGpxString } from './exporters/Gpx';
import Display, { Display2 } from './components/Display';
import { VirtualHRSensor, VirtualPowermeter, VirtualTrainer } from './sensors/fake/components/VirtualSensors';
import Graph from './components/Graph';
import WorkoutView from './workouts/WorkoutView';
import { Workout } from './workouts/Workout';
import { WorkoutRepository } from './workouts/WorkoutRepository';
import IntervalProgressView from './workouts/IntervalProgressView';
import { AppBar, Box, Button, Dialog, Icon, IconButton, ThemeProvider, Toolbar, Tooltip, Typography, createTheme } from '@mui/material';
import SettingsDialog from './settings/SettingsDialog';
import Settings from './settings/Settings';
import SettingsIcon from '@mui/icons-material/Settings';
import { Bolt, FormatListNumbered, HeartBroken } from '@mui/icons-material';
import WorkoutSelectorDialog from './workouts/WorkoutSelectorDialog';
import WorkoutPlayer from './workouts/WorkoutPlayer';

class AppState {
  heartRateSensorName: string | null = null;
  heartRate: number | null = null;
  cyclingPowerSensorName: string | null = null;
  cyclingPower: number | null = null;
  cyclingCadence: number | null = null;

  isPaused: boolean = false;
  isActivityStarted: boolean = false;

  activityTotalSeconds: number = 0;

  targetCyclingPower: number = 0;
  targetCyclingCadence: number = 60;

  isSettingsOpen: boolean = false;
  isWorkoutSelectorOpen: boolean = false;

  SettingUseVirtualSensors: boolean = false;
  DEBUG: boolean = false;
};

class App extends React.Component<any, AppState>
{
  SensorManager : SensorManager = new SensorManager();
  Recorder: Recorder = new Recorder();
  //Workout: Workout = WorkoutRepository.SST;
  WorkoutPlayer: WorkoutPlayer = new WorkoutPlayer(WorkoutRepository.SST);
  Settings: Settings = new Settings();

  constructor(props: any)
  {
    super(props);
    this.state = new AppState();

    this.loadSettingsFromLocalStorage();
    this.WorkoutPlayer.Ftp = this.Settings.UserDataFtp;

    if (SensorManager.UseVirtualSensors != this.state.SettingUseVirtualSensors)
      SensorManager.UseVirtualSensors = this.state.SettingUseVirtualSensors;

    this.Recorder.onActivityChanged = this.onActivityChanged.bind(this);

    // Install a hook so that App state can be changed from browser's console.
    (window as any).setAppState = ((stateObject: any) => this.setState(stateObject));
  }

  componentDidUpdate(prevProps: Readonly<any>, prevState: Readonly<AppState>, snapshot?: any): void
  {
    if (SensorManager.UseVirtualSensors != this.state.SettingUseVirtualSensors)
      SensorManager.UseVirtualSensors = this.state.SettingUseVirtualSensors;
  }

  async onSearchHRSensor()
  {
    var sensor = await this.SensorManager.SearchHRSensor();
    this.setState({heartRateSensorName : sensor.name});
    sensor.onDisconnected = this.clearHRData.bind(this);
    await sensor.start(this.onHRDataReceived.bind(this));
  };

  disconnectHRSensor()
  {
    this.SensorManager.HRSensor?.disconnect();
    this.clearHRData();
  }

  private onHRDataReceived(hr: number): void 
  {
    this.Recorder.Values.HR = hr;
    this.setState({heartRate: hr})
  }
  
  private clearHRData() {
    this.Recorder.Values.HR = null;
    this.setState(
      {
        heartRateSensorName: null,
        heartRate: null
      });
  }

  async onSearchTrainer()
  {
    var trainer = await this.SensorManager.SearchTrainer();
    this.setState({cyclingPowerSensorName : trainer.name});
    trainer.onDisconnected = this.clearCPData.bind(this);
    trainer.onCyclingPowerDataReceived = this.onCPDataReceived.bind(this);
    trainer.connect();
  }

  onCPDataReceived(data: CyclingPowerEventData) {
    this.Recorder.Values.Power = data.Power;
    this.Recorder.Values.Cadence = data.CrankRpm;
    this.setState({cyclingPower: data.Power, cyclingCadence: data.CrankRpm})
  }

  disconnectTrainer()
  {
    this.SensorManager.Trainer?.disconnect();
    this.clearCPData();
  }

  private clearCPData() {
    this.Recorder.Values.Power = null;
    this.Recorder.Values.Cadence = null;

    this.setState(
      {
        cyclingPowerSensorName: null,
        cyclingPower: null,
        cyclingCadence: null
      });
  }

  private onActivityChanged(activity: Activity) {
    this.setState({activityTotalSeconds: activity.TotalSeconds});
    this.WorkoutPlayer.CurrentTime = activity.TotalSeconds;

    const currentPower = this.WorkoutPlayer.CurrentPowerNormalized;
    if (currentPower === undefined)
      return;
    
    if (this.state.targetCyclingPower != currentPower && currentPower != undefined)
    {
      this.setState({
        targetCyclingPower: currentPower
      });
      this.SensorManager.Trainer?.setTargetPower(currentPower);
    }
  }

  private startActivity() {
    this.Recorder.startRecordingNewActivity();
    this.WorkoutPlayer.startWorkout();

    this.setState({ isActivityStarted: true, isPaused: false});
  }

  private toggleIsPaused() {
    this.Recorder.togglePauseRecording();
    this.setState((state) => ({isPaused: this.Recorder.IsPaused}));
  }
  
  private endActivity() {
    this.Recorder.stopRecordingActivity();

    // TODO: save activity
    this.setState({ isActivityStarted: false, isPaused: false, activityTotalSeconds: 0});
  }

  private async onSave() {
    //console.log("Save");

    if (this.Recorder.Activity == null)
      return;
    
    const xml = ActivityToGpxString(this.Recorder.Activity);

    // var blob = new Blob([xml], {type: "application/octet-stream"});
    const blob = new Blob([xml], {type: "application/gpx+xml"});

    const date = this.Recorder.Activity.StartTime;
    
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Months are 0-based in JavaScript
    const day = date.getDate().toString().padStart(2, '0');
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');

    const fileName = `PBR Virtual Ride ${date.getFullYear()}-${month}-${day} ${hours}-${minutes}-${seconds}.gpx`;

    FileSaver.saveAs(blob, fileName);
  }

  private async setTargetPower(targetPower: number) {
    this.SensorManager.Trainer?.setTargetPower(targetPower);
  }

  private async setResistance(resistance: number) {
    this.SensorManager.Trainer?.setTargetResistance(resistance);
  }

  private static readonly darkTheme = createTheme({
    palette: {
      mode: 'dark',
      primary: {
        main: '#91a47d',
      },
    },
    components: {
        MuiTextField: {
            styleOverrides: {
                root: {
                    width: '7ch',
                    margin: '1rem',
                    fontSize: '2rem',
                },

            }
        },
    }
  });

  render() {
    return (
      <ThemeProvider theme={App.darkTheme}>
        <div className="App">
          <AppBar position="static">
            <Toolbar>
              <Box sx={{ml: -2}}>
                <span className="App-title-logo">PBR</span>
                <span style={{fontSize: "0.5rem"}}>v0.0.8</span>
              </Box>

              <div style={{flexGrow: 1}} />

              {this.state.heartRateSensorName == null ?
                <Tooltip title="Connect a heart rate sensor">
                  <IconButton className='button disconnected' sx={{marginRight: "0.5rem"}} onClick={this.onSearchHRSensor.bind(this)}>
                    <HeartBroken sx={{transform: 'scale(1.2)'}} />
                  </IconButton>
                </Tooltip> :
                <Tooltip title="Disconnect the heart rate sensor">
                  <IconButton className='button' sx={{marginRight: "0.5rem"}} onClick={this.disconnectHRSensor.bind(this)}>
                    <HeartBroken sx={{transform: 'scale(1.2)'}} />
                  </IconButton>
                </Tooltip>
              }

              {this.state.cyclingPowerSensorName == null ?
                <Tooltip title="Connect a power meter or cycling trainer">
                  <IconButton className='button disconnected' sx={{marginRight: "0.5rem"}} onClick={this.onSearchTrainer.bind(this)}>
                    <Bolt sx={{transform: 'scale(1.6)'}} />
                  </IconButton>
                </Tooltip> :
                <Tooltip title="Disconnect the power meter or the cycling trainer">
                  <IconButton className='button' sx={{marginRight: "0.5rem"}} onClick={this.disconnectTrainer.bind(this)}>
                    <Bolt sx={{transform: 'scale(1.6)'}} />
                  </IconButton>
                </Tooltip>
              }

              <Tooltip title="Settings">
                <IconButton className='button' onClick={() => this.setState({isSettingsOpen: true})} edge="end">
                  <SettingsIcon className='larger-svg-icon' />
                </IconButton>
              </Tooltip>
            </Toolbar>
            </AppBar>

          <div className="App-content">
          {this.state.DEBUG && 
              <div>
                <button className='button' onClick={() => this.setTargetPower(0)}>0 W</button>
                <button className='button' onClick={() => this.setTargetPower(1)}>1 W</button>
                <button className='button' onClick={() => this.setTargetPower(100)}>100 W</button>
                <button className='button' onClick={() => this.setTargetPower(200)}>200 W</button>
                <button className='button' onClick={() => this.setTargetPower(300)}>300 W</button>
                <button className='button' onClick={() => this.setTargetPower(400)}>400 W</button>
                <button className='button' onClick={() => this.setTargetPower(1000)}>1k W</button>
                <button className='button' onClick={() => this.setTargetPower(4000)}>4k W</button>
                <br />
                <button className='button' onClick={() => this.setResistance(0)}>R 0</button>
                <button className='button' onClick={() => this.setResistance(1)}>R 1</button>
                <button className='button' onClick={() => this.setResistance(50)}>R 50</button>
                <button className='button' onClick={() => this.setResistance(100)}>R 100</button>
                <button className='button' onClick={() => this.setResistance(200)}>R 200</button>
                <button className='button' onClick={() => this.setResistance(201)}>R 201</button>
                <button className='button' onClick={() => this.setResistance(255)}>R 255</button>
              </div> }
          </div>
            <Graph activity={this.Recorder.Activity} />

          <div className="App-footer">

            <div className="App-footer-group" style={{alignSelf: 'flex-end'}}>
              <div style={{display: 'flex', flexDirection: 'column'}}>
                <WorkoutView workoutPlayer={this.WorkoutPlayer} />
                <div>
                  <IconButton className='button' onClick={() => this.setState({isWorkoutSelectorOpen: true})}>
                    <FormatListNumbered />
                  </IconButton>
                </div>
              </div>
            </div>

            <div className="App-footer-group" style={{alignSelf: 'flex-end', columnGap: "2rem"}}>
              <div className="App-footer-group2">
                <Display2 header='INTERVAL TIME'>
                  {this.WorkoutPlayer.CurrentInterval 
                  && MillisToHMS(
                      (this.WorkoutPlayer.CurrentTime - (this.WorkoutPlayer.TimeAtBeginningOfCurrentInterval ?? 0)) * 1000)
                  }
                </Display2>
                {/* <IntervalProgressView TotalSeconds={this.state.workout.CurrentInterval?.TotalSeconds ?? 0} 
                                      CurrentSecond={this.state.workout.CurrentTime - (this.state.workout.TimeAtBeginningOfCurrentInterval ?? 0)} /> */}
                <Display2 header='TARGET POWER'>{this.WorkoutPlayer.CurrentPowerNormalized}</Display2>
                <Display2 header='TARGET CADENCE'>{this.WorkoutPlayer.CurrentCadence}</Display2>
              </div>

              <div className="App-footer-group2">
                <Display2 header='HEART RATE'>{this.state.heartRate}</Display2>
                <Display2 header='POWER'>{this.state.cyclingPower}</Display2>
                <Display2 header='CADENCE'>{this.state.cyclingCadence}</Display2>
              </div>

              <div className="App-footer-group2">
                <Display2 header='TOTAL TIME'>{MillisToHMS(this.state.activityTotalSeconds * 1000)}</Display2>
                <Display2 header='DISTANCE'></Display2>
                <Display2 header='SPEED'></Display2>
              </div>

            </div>

            <div className='App-footer-group' style={{alignSelf: 'flex-end'}}>
              <div className='App-footer-group2'>

              {!this.state.isActivityStarted &&
                <button className="button button-big" onClick={this.startActivity.bind(this)}>Start</button>}
              {this.state.isActivityStarted &&
                <>
                  {!this.state.isPaused &&
                    <button className="button button-big" onClick={this.toggleIsPaused.bind(this)}>Pause</button>}
                  {this.state.isPaused &&
                    <>
                      <button className='button button-big' onClick={this.onSave.bind(this)}>Save</button>
                      <button className="button button-big" onClick={this.endActivity.bind(this)}>End</button>
                      <button className="button button-big" onClick={this.toggleIsPaused.bind(this)}>Resume</button>
                    </>
                  }
                </>
              }
              </div>
            </div>
          </div>

          {this.state.SettingUseVirtualSensors &&
          <div className="floatingOverlay">
            <VirtualHRSensor />
            {/* <VirtualPowermeter /> */}
            <VirtualTrainer />
          </div>
          }

          <SettingsDialog open={this.state.isSettingsOpen} handleClose={this.onSettingsClosed.bind(this)}
            UserFtp={this.Settings.UserDataFtp} />

          <WorkoutSelectorDialog open={this.state.isWorkoutSelectorOpen} 
            handleClose={() => this.setState({isWorkoutSelectorOpen: false})} 
            workoutSelected={this.onWorkoutChanged.bind(this)} />
        </div>
      </ThemeProvider>
    );
  }

  private onSettingsClosed(success: boolean, settings: Settings) {
    if (success)
    {
      this.Settings = settings;
      this.WorkoutPlayer.Ftp = settings.UserDataFtp;
      this.forceUpdate();
      this.saveSettingsToLocalStorage();
    }

    this.setState({isSettingsOpen: false});
  }

  private saveSettingsToLocalStorage() {
    console.log(this.Settings);
    localStorage.setItem("settings", JSON.stringify(this.Settings));
  }

  private loadSettingsFromLocalStorage() {
    const settings = localStorage.getItem("settings");
    console.log(settings);
    if (settings != null)
      this.Settings = JSON.parse(settings);
  }

  onWorkoutChanged(workout: Workout) {
    this.WorkoutPlayer = new WorkoutPlayer(workout);
    this.WorkoutPlayer.Ftp = this.Settings.UserDataFtp;
    this.forceUpdate();
  }
}

export default App;